import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { ITableAvailability, ReservationSource } from "../models/IReservation";
import { createStaffReservation, getTableAvailability } from "../services/reservationService";
import DateTimePicker from "../../components/DateTimePicker";
import { isBelowMinCapacity, isTableSelectable } from "../utils/tableAvailability";

const DURATION_PRESETS = [60, 90, 120, 180];

interface Props {
  restaurantId: string;
  onClose: () => void;
  /** Oluşan rezervasyonun başlangıcı (UTC ISO) — çağıran taraf programı o güne alabilsin diye. */
  onCreated: (startsAtUtc: string) => void;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

function reasonLabel(t: ITableAvailability): string {
  switch (t.unavailableReason) {
    case "RestaurantClosed":
      return "Restoran bu saatte kapalı";
    case "Booked": {
      const b = t.bookings?.[0];
      return b ? `${formatTime(b.startsAtUtc)}–${formatTime(b.endsAtUtc)} dolu` : "Dolu";
    }
    case "TooSmall":
      return `En fazla ${t.capacity} kişi`;
    case "TooLarge":
      return `En az ${t.minCapacity} kişilik`;
    default:
      return "Uygun değil";
  }
}

/** datetime-local değerini (yerel saat) UTC ISO'ya çevirir. */
function localToUtcIso(local: string): string {
  return new Date(local).toISOString();
}

/** Bir sonraki yarım saate yuvarlanmış "şimdi"yi datetime-local biçiminde verir. */
function defaultStartLocal(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + 30 - (d.getMinutes() % 30));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const ERROR_TEXT: Record<string, string> = {
  ReservationsNotEnabled: "Bu restoran rezervasyon almıyor. Önce Ayarlar sekmesinden rezervasyonu açın.",
  StartsInThePast: "Seçilen saat geçmişte kalıyor.",
  TooFarAhead: "Seçilen tarih, restoranın kabul ettiği en uzak tarihten daha ileride.",
  TableNotFound: "Masa bulunamadı.",
  TableNotInRestaurant: "Seçilen masa bu restorana ait değil.",
  TableRetired: "Seçilen masa kullanım dışı.",
  TableDoesNotFitParty: "Seçilen masa bu kişi sayısına uygun değil.",
  RestaurantClosedAtThatTime: "Restoran bu saat aralığında kapalı.",
  TableAlreadyBooked: "Bu masa az önce doldu. Liste güncellendi, lütfen başka bir masa seçin.",
  RestourantAccessDenied: "Bu restoranda rezervasyon oluşturma yetkiniz yok.",
};

const inputStyle: CSSProperties = { borderRadius: 10, border: "1.5px solid #e5e7eb" };
const labelStyle: CSSProperties = { fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 };

export default function CreateReservationModal({ restaurantId, onClose, onCreated }: Props) {
  const [startLocal, setStartLocal] = useState(defaultStartLocal());
  const [partySize, setPartySize] = useState("2");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [note, setNote] = useState("");
  const [source, setSource] = useState<ReservationSource>("Phone");
  const [tableId, setTableId] = useState("");

  const [availability, setAvailability] = useState<ITableAvailability[] | null>(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [availError, setAvailError] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const party = Number(partySize);
  const duration = Number(durationMinutes);
  const canQuery = !!startLocal && party > 0;

  const fetchAvailability = () => {
    if (!canQuery) { setAvailability(null); return; }
    setAvailLoading(true);
    setAvailError("");
    getTableAvailability(restaurantId, localToUtcIso(startLocal), party, duration > 0 ? duration : undefined)
      .then((res) => {
        const list = res.data ?? [];
        setAvailability(list);
        setTableId((prev) => (list.some((t) => t.id === prev && isTableSelectable(t)) ? prev : ""));
      })
      .catch(() => setAvailError("Masa uygunluğu alınamadı."))
      .finally(() => setAvailLoading(false));
  };

  // Tarih/kişi/süre değiştikçe uygunluğu tazele
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchAvailability, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startLocal, partySize, durationMinutes]);

  const allClosed = !!availability && availability.length > 0
    && availability.every((t) => t.unavailableReason === "RestaurantClosed");

  const availableCount = availability?.filter(isTableSelectable).length ?? 0;

  // Masa arama: numara veya etikete göre filtrele
  const visibleTables = useMemo(() => {
    let list = availability ?? [];
    if (onlyAvailable) list = list.filter(isTableSelectable);
    const q = tableSearch.trim().toLocaleLowerCase("tr");
    if (q) {
      list = list.filter((t) =>
        String(t.tableNumber).includes(q) || (t.label ?? "").toLocaleLowerCase("tr").includes(q)
      );
    }
    return list;
  }, [availability, tableSearch, onlyAvailable]);

  // Seçilen masa ve bitiş saati önizlemesi
  const selectedTable = availability?.find((t) => t.id === tableId) ?? null;
  const endPreview = useMemo(() => {
    if (!startLocal) return "";
    const start = new Date(startLocal);
    if (isNaN(start.getTime())) return "";
    const mins = duration > 0 ? duration : 0;
    if (!mins) return "";
    const end = new Date(start.getTime() + mins * 60000);
    const f = (d: Date) => d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
    return `${f(start)} – ${f(end)}`;
  }, [startLocal, duration]);

  const handleSubmit = () => {
    if (!contactName.trim()) { setError("Misafir adı zorunludur."); return; }
    if (contactPhone.trim().length < 5) { setError("Geçerli bir telefon numarası girin."); return; }
    if (!startLocal) { setError("Tarih ve saat seçin."); return; }
    if (!(party > 0)) { setError("Kişi sayısı en az 1 olmalı."); return; }
    if (!tableId) { setError("Lütfen müsait bir masa seçin."); return; }

    setSaving(true);
    setError("");
    createStaffReservation({
      restourantId: restaurantId,
      tableId,
      startsAtUtc: localToUtcIso(startLocal),
      partySize: party,
      contactName: contactName.trim(),
      contactPhone: contactPhone.trim(),
      note: note.trim() || undefined,
      durationMinutes: duration > 0 ? duration : undefined,
      source,
    })
      .then((res) => {
        onCreated(res.data?.startsAtUtc ?? localToUtcIso(startLocal));
        onClose();
      })
      .catch((err) => {
        const code = err?.response?.data?.[0]?.code;
        setError(ERROR_TEXT[code] ?? "Rezervasyon oluşturulamadı. Lütfen tekrar deneyin.");
        if (code === "TableAlreadyBooked" || code === "RestaurantClosedAtThatTime") fetchAvailability();
      })
      .finally(() => setSaving(false));
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)", padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: 20, width: "min(96vw, 520px)", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>

        {/* Başlık */}
        <div style={{ padding: "22px 26px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff", zIndex: 1, borderBottom: "1.5px solid #f1f5f9" }}>
          <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "#f0fdfa", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <i className="bi bi-calendar-plus-fill" style={{ color: "#0d9488", fontSize: "1.05rem" }}></i>
            </div>
            <div>
              <div>Yeni Rezervasyon</div>
              <div style={{ fontSize: "0.72rem", fontWeight: 500, color: "#9ca3af" }}>Telefon / kapı kaydı — doğrudan onaylı açılır</div>
            </div>
          </div>
          <button onClick={onClose} disabled={saving} style={{ background: "none", border: "none", cursor: saving ? "not-allowed" : "pointer", color: "#9ca3af", fontSize: "1.1rem" }}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div style={{ padding: "18px 26px 24px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Misafir bilgileri */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 200px" }}>
              <label style={labelStyle}>Misafir Adı <span style={{ color: "#dc2626" }}>*</span></label>
              <input className="form-control" style={inputStyle} value={contactName} maxLength={100}
                onChange={(e) => setContactName(e.target.value)} placeholder="Ad Soyad" />
            </div>
            <div style={{ flex: "1 1 170px" }}>
              <label style={labelStyle}>Telefon <span style={{ color: "#dc2626" }}>*</span></label>
              <input className="form-control" style={inputStyle} value={contactPhone} maxLength={20}
                onChange={(e) => setContactPhone(e.target.value)} placeholder="+905551112233" />
            </div>
          </div>
          <div style={{ fontSize: "0.72rem", color: "#9ca3af", marginTop: -8 }}>
            <i className="bi bi-info-circle me-1"></i>
            Detay ve hatırlatma SMS'i bu numaraya gider — yanlış numara misafirin kendi iptalini de engeller.
          </div>

          {/* Tarih & saat */}
          <div>
            <label style={labelStyle}>Tarih & Saat <span style={{ color: "#dc2626" }}>*</span></label>
            <DateTimePicker value={startLocal} onChange={setStartLocal} />
          </div>

          {/* Kişi sayısı */}
          <div>
            <label style={labelStyle}>Kişi Sayısı <span style={{ color: "#dc2626" }}>*</span></label>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", border: "1.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                <button type="button" onClick={() => setPartySize(String(Math.max(1, party - 1)))}
                  style={{ width: 36, height: 38, border: "none", background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: "1rem" }}>
                  <i className="bi bi-dash-lg"></i>
                </button>
                <input type="number" min={1} max={500} value={partySize}
                  onChange={(e) => setPartySize(e.target.value)}
                  style={{ width: 62, height: 38, border: "none", textAlign: "center", fontWeight: 700, color: "#111827", outline: "none" }} />
                <button type="button" onClick={() => setPartySize(String(Math.min(500, (party || 0) + 1)))}
                  style={{ width: 36, height: 38, border: "none", background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: "1rem" }}>
                  <i className="bi bi-plus-lg"></i>
                </button>
              </div>
              <div className="resv-chips">
                {[2, 4, 6, 8].map((n) => (
                  <button key={n} type="button" onClick={() => setPartySize(String(n))}
                    className={`resv-chip${party === n ? " resv-chip--active" : ""}`}>{n} kişi</button>
                ))}
              </div>
            </div>
          </div>

          {/* Masada kalış süresi */}
          <div>
            <label style={labelStyle}>Masada Kalış Süresi</label>
            <div className="resv-chips">
              <button type="button" onClick={() => setDurationMinutes("")}
                className={`resv-chip${!durationMinutes ? " resv-chip--active" : ""}`}>
                Restoranın varsayılanı
              </button>
              {DURATION_PRESETS.map((m) => (
                <button key={m} type="button" onClick={() => setDurationMinutes(String(m))}
                  className={`resv-chip${duration === m ? " resv-chip--active" : ""}`}>
                  {m >= 60 ? `${m / 60} saat${m % 60 ? ` ${m % 60} dk` : ""}` : `${m} dk`}
                </button>
              ))}
            </div>
            <div style={{ fontSize: "0.72rem", color: "#9ca3af", marginTop: 6 }}>
              <i className="bi bi-info-circle me-1"></i>
              Masanın bu rezervasyon için ne kadar süre dolu sayılacağını belirler.
              {endPreview && <> Masa <strong style={{ color: "#0f766e" }}>{endPreview}</strong> arası dolu olacak.</>}
            </div>
          </div>

          {/* Kaynak */}
          <div>
            <label style={labelStyle}>Kayıt Kaynağı</label>
            <div style={{ display: "flex", gap: 8 }}>
              {([["Phone", "Telefon", "bi-telephone-fill"], ["Panel", "Panel / Kapı", "bi-door-open-fill"]] as [ReservationSource, string, string][]).map(([val, label, icon]) => (
                <button key={val} type="button" onClick={() => setSource(val)}
                  style={{
                    flex: 1, padding: "9px 12px", borderRadius: 10, cursor: "pointer",
                    border: `1.5px solid ${source === val ? "#0d9488" : "#e5e7eb"}`,
                    background: source === val ? "#f0fdfa" : "#fff",
                    color: source === val ? "#0f766e" : "#6b7280",
                    fontWeight: 700, fontSize: "0.82rem",
                  }}>
                  <i className={`bi ${icon} me-1`}></i>{label}
                </button>
              ))}
            </div>
          </div>

          {/* Masa seçimi */}
          <div>
            <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 8 }}>
              <span>Masa Seç <span style={{ color: "#dc2626" }}>*</span></span>
              {availability && !availLoading && (
                <span style={{ fontWeight: 600, color: availableCount > 0 ? "#0f766e" : "#b45309", fontSize: "0.74rem" }}>
                  · {availableCount} müsait / {availability.length} masa
                </span>
              )}
              {availLoading && <span className="spinner-border spinner-border-sm text-secondary" style={{ width: 13, height: 13 }} />}
            </label>

            {availError && (
              <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10, padding: "8px 12px", color: "#b91c1c", fontSize: "0.8rem", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span><i className="bi bi-exclamation-circle me-1"></i>{availError}</span>
                <button onClick={fetchAvailability} style={{ background: "none", border: "none", color: "#b91c1c", fontWeight: 700, cursor: "pointer", fontSize: "0.78rem", flexShrink: 0 }}>Tekrar Dene</button>
              </div>
            )}

            {allClosed && (
              <div style={{ background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 10, padding: "8px 12px", color: "#92400e", fontSize: "0.8rem", marginBottom: 8 }}>
                <i className="bi bi-exclamation-triangle-fill me-1"></i>Restoran bu saatte kapalı — hiçbir masa uygun değil.
              </div>
            )}

            {!canQuery ? (
              <div style={{ fontSize: "0.8rem", color: "#9ca3af", padding: "10px 0" }}>
                Masaları görmek için tarih, saat ve kişi sayısını girin.
              </div>
            ) : availability && !availLoading ? (
              <>
                {availability.length > 6 && (
                  <div className="resv-search">
                    <i className="bi bi-search"></i>
                    <input
                      type="text"
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                      placeholder="Masa numarası veya adı ile ara..."
                    />
                    {tableSearch && (
                      <button type="button" className="resv-search__clear" onClick={() => setTableSearch("")} aria-label="Temizle">
                        <i className="bi bi-x-lg"></i>
                      </button>
                    )}
                  </div>
                )}

                {availability.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <button type="button" onClick={() => setOnlyAvailable((v) => !v)}
                      className={`resv-chip${onlyAvailable ? " resv-chip--active" : ""}`}>
                      <i className={`bi ${onlyAvailable ? "bi-check-square-fill" : "bi-square"} me-1`}></i>
                      Sadece müsait masalar
                    </button>
                  </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: 7, maxHeight: 220, overflowY: "auto", border: "1.5px solid #e5e7eb", borderRadius: 12, padding: 8 }}>
                {availability.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "16px 0", color: "#9ca3af", fontSize: "0.82rem" }}>
                    Restorana ait masa bulunamadı.
                  </div>
                ) : visibleTables.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "16px 0", color: "#9ca3af", fontSize: "0.82rem" }}>
                    {tableSearch ? `"${tableSearch}" ile eşleşen masa yok.` : "Filtreye uyan masa yok."}
                  </div>
                ) : (
                  visibleTables.map((t) => {
                    const selected = tableId === t.id;
                    const disabled = !isTableSelectable(t);
                    const belowMin = isBelowMinCapacity(t);
                    return (
                      <button key={t.id} type="button" disabled={disabled} onClick={() => setTableId(t.id)}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                          padding: "9px 12px", borderRadius: 9, textAlign: "left",
                          border: `1.5px solid ${selected ? "#0d9488" : disabled ? "#f1f5f9" : "#e5e7eb"}`,
                          background: selected ? "#f0fdfa" : disabled ? "#f9fafb" : "#fff",
                          cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.65 : 1,
                          transition: "border-color 0.15s, background 0.15s",
                        }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: "0.85rem", color: disabled ? "#9ca3af" : "#111827" }}>
                            Masa {t.tableNumber}{t.label ? ` — ${t.label}` : ""}
                          </div>
                          <div style={{ fontSize: "0.74rem", color: "#9ca3af" }}>{t.minCapacity}–{t.capacity} kişi</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                          {(disabled || belowMin) && (
                            <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "#b45309", background: "#fffbeb", padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap" }}>
                              {belowMin && <i className="bi bi-exclamation-triangle me-1"></i>}
                              {reasonLabel(t)}
                            </span>
                          )}
                          {selected && <i className="bi bi-check-circle-fill" style={{ color: "#0d9488" }}></i>}
                        </div>
                      </button>
                    );
                  })
                )}
                </div>
              </>
            ) : null}

            {selectedTable && isBelowMinCapacity(selectedTable) && (
              <div style={{ background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 10, padding: "8px 12px", color: "#92400e", fontSize: "0.8rem", marginTop: 8, display: "flex", gap: 8 }}>
                <i className="bi bi-exclamation-triangle-fill" style={{ flexShrink: 0, marginTop: 1 }}></i>
                <span>
                  Masa {selectedTable.tableNumber} minimum <b>{selectedTable.minCapacity} kişiliktir</b>, bu rezervasyon {party} kişi.
                  Yine de kaydederseniz rezervasyon bu masaya atanır.
                </span>
              </div>
            )}
          </div>

          {/* Not */}
          <div>
            <label style={labelStyle}>Not</label>
            <textarea className="form-control" style={{ ...inputStyle, resize: "vertical" }} rows={2} maxLength={500}
              value={note} onChange={(e) => setNote(e.target.value)} placeholder="Örn. pencere kenarı olsun" />
          </div>

          {/* Özet — personel misafire tekrar okuyabilsin */}
          {selectedTable && startLocal && (
            <div style={{ background: "#f0fdfa", border: "1.5px solid #99f6e4", borderRadius: 12, padding: "10px 14px", fontSize: "0.82rem", color: "#0f766e" }}>
              <i className="bi bi-clipboard-check-fill me-2"></i>
              <strong>{contactName.trim() || "Misafir"}</strong> ·{" "}
              {new Date(startLocal).toLocaleString("tr-TR", { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })} ·{" "}
              {party} kişi · Masa {selectedTable.tableNumber}{selectedTable.label ? ` (${selectedTable.label})` : ""}
              {endPreview && ` · ${endPreview}`}
            </div>
          )}

          {error && (
            <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10, padding: "8px 12px", color: "#b91c1c", fontSize: "0.82rem" }}>
              <i className="bi bi-exclamation-circle me-1"></i>{error}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <button onClick={onClose} disabled={saving}
              style={{ padding: "9px 18px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280", fontWeight: 600, fontSize: "0.85rem", cursor: saving ? "not-allowed" : "pointer" }}>
              Vazgeç
            </button>
            <button onClick={handleSubmit} disabled={saving || !tableId}
              style={{
                padding: "9px 22px", borderRadius: 10, border: "none",
                background: saving || !tableId ? "#e5e7eb" : "linear-gradient(135deg, #0d9488, #0f766e)",
                color: saving || !tableId ? "#9ca3af" : "#fff", fontWeight: 700, fontSize: "0.85rem",
                cursor: saving || !tableId ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 7,
              }}>
              {saving
                ? <><span className="spinner-border spinner-border-sm"></span> Kaydediliyor...</>
                : <><i className="bi bi-check-lg"></i> Rezervasyonu Oluştur</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
